/**
 * Alloy Loader
 * 
 * This file handles dynamically loading and initializing the Adobe Alloy SDK.
 * It follows the same pattern as zoltar-demo-pilot's implementation.
 */

const ALLOY_CDN_URL = process.env.REACT_APP_ALLOY_CDN_URL || 'https://cdn1.adoberesources.net/alloy/2.27.0/alloy.js';

let isConfigured = false;

let initializationPromise = null;

function initializeAlloyNamespace() {
  const instanceNames = ['alloy'];

  instanceNames.forEach((instanceName) => {
    if (!window[instanceName]) {
      window.__alloyNS = window.__alloyNS || [];
      if (!window.__alloyNS.includes(instanceName)) {
        window.__alloyNS.push(instanceName);
      }
      const alloyFunction = function() {
        const args = arguments;
        return new Promise((resolve, reject) => {
          window.setTimeout(() => {
            const currentAlloyFn = window[instanceName];
            if (currentAlloyFn && currentAlloyFn.q) {
              currentAlloyFn.q.push([resolve, reject, args]);
            }
          });
        });
      };

      alloyFunction.q = [];
      window[instanceName] = alloyFunction;
    }
  });
}

/**
 * @returns {Promise<void>}
 */
function loadAlloyScript() {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src*="alloy.js"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = ALLOY_CDN_URL;
    script.async = true;

    script.onload = () => {
      console.log('Alloy script loaded successfully');
      resolve();
    };

    script.onerror = () => {
      reject(new Error('Failed to load Alloy library from CDN'));
    };

    document.head.appendChild(script);
  });
}

/**
 * Main initialization function for Alloy
 * 
 * @param {Object} config - Alloy configuration object
 * @param {string} config.datastreamId - The Edge datastream ID
 * @param {string} config.orgId - The Adobe Organization ID
 * @param {boolean} [config.debugEnabled] 
 * @returns {Promise<void>}
 */
export async function initializeAlloy(config) {
  if (typeof window === 'undefined') {
    console.warn('Alloy cannot be initialized in non-browser environment');
    return;
  }
  if (isConfigured) {
    console.log('Alloy already configured, skipping');
    return;
  }

  if (initializationPromise) {
    console.log('Alloy initialization in progress, waiting...');
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      initializeAlloyNamespace();

      await loadAlloyScript();

      if (config && window.alloy && !isConfigured) {
        await window.alloy('configure', {
          datastreamId: config.datastreamId,
          orgId: config.orgId,
          debugEnabled: config.debugEnabled || false,
        });

        isConfigured = true;

        console.log('Alloy configured successfully', {
          datastreamId: config.datastreamId,
          orgId: config.orgId,
          debugEnabled: config.debugEnabled,
        });
      }
    } catch (error) {
      initializationPromise = null;
      console.error('Failed to initialize Alloy:', error);
      throw error;
    }
  })();

  return initializationPromise;
}

/**
 * @returns {boolean}
 */
export function isAlloyConfigured() {
  return isConfigured && typeof window !== 'undefined' && typeof window.alloy === 'function';
}

/**
 * Export the alloy function for use in other files
 * 
 * @returns {Function} 
 * @throws {Error} 
 */
export const alloy = () => {
  if (!window.alloy) {
    throw new Error('Alloy not initialized. Call initializeAlloy() first.');
  }
  return window.alloy;
};
